import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { CrudOperation, User } from '@shared/models';
import { AppNotificationService, ModalService } from '@shared/services';
import { CurrentUserService } from '@shared/services/current-user.service';
import { UserService } from '@shared/services/user.service';
import { difference } from 'lodash-es';
import { QueryOptions } from 'odata-query';
import {
  concatAll,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { isGstarSystemUser } from '../../helpers/is-gstar-system-user';
import {
  isSignedInUsersRow,
  signedInSnapshot,
} from '../../helpers/is-signed-in-users-row';
import {
  Client,
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
  InternalUser,
  InternalUserRole,
  InternalUserWithRoles,
  UserCountryAssignment,
  UserEngagementAssignment,
} from '../../models';
import { ClientService } from '../../services';
import { InternalUserService } from '../../services/internal-user.service';

@Component({
  standalone: false,
  selector: 'gstar-internal-users-page-component',
  templateUrl: 'internal-users-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalUsersPageComponent {
  private readonly internalUserService = inject(InternalUserService);
  private readonly userService = inject(UserService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clientService = inject(ClientService);

  public entities: WritableSignal<Collection<InternalUser>> = signal({
    total: 0,
    items: [],
  });

  usersQuery: Partial<QueryOptions<User>> = {
    filter: { isActive: true },
    orderBy: 'firstName',
  };

  public query: Partial<QueryOptions<InternalUser>> = {};

  private entityName = this.translateService.instant(
    'admin.dashboard.internalUserTitleNoticationEntity'
  );

  create(
    internalUserGridItem: GridItemCreateEvent<InternalUserWithRoles>
  ): void {
    let isSuccess = false;
    this.internalUserService
      .create(internalUserGridItem.item)
      .pipe(
        switchMap(({ id }: InternalUser) => {
          const arrayObs$ = this.buildCreateRoleAndCountryObs(
            id,
            internalUserGridItem.item
          );
          return forkJoin([from(arrayObs$).pipe(concatAll())]);
        }),
        switchMap(() => this.userService.getAllUser(this.usersQuery))
      )
      .subscribe(() => {
        internalUserGridItem.onSuccess();
        isSuccess = true;
        this.fetchEntities();

        this.appNotificationService.crudNotify(
          CrudOperation.Create,
          this.entityName,
          isSuccess
        );
      });
  }

  update(
    internalUserGridItem: GridItemUpdateEvent<InternalUserWithRoles>
  ): void {
    let isSuccess = false;
    const item = internalUserGridItem.item!;
    const prevItem = internalUserGridItem.prevItem!;
    const countryAdminRoleId = item.countryAdminRoleId;

    const sourceKeys = Object.keys(prevItem.userRoles).filter(
      key => prevItem.userRoles[key]
    );
    const targetKeys = Object.keys(item.userRoles).filter(
      key => item.userRoles[key]
    );

    const addedUserRoleIds = difference(targetKeys, sourceKeys).filter(
      roleId => roleId !== countryAdminRoleId
    );
    const removedUserRoleIds = difference(sourceKeys, targetKeys).filter(
      roleId => roleId !== countryAdminRoleId
    );

    const countryAdminOps = this.buildCountryAdminSaveObs(item, prevItem);

    const removeOps$ = [
      ...removedUserRoleIds.map(userRoleId =>
        this.internalUserService.removeFromUserRole(item.id, userRoleId)
      ),
      ...countryAdminOps.removes,
    ];

    const addOps$ = [
      ...addedUserRoleIds.map(userRoleId =>
        this.internalUserService.addToUserRole(item.id, userRoleId)
      ),
      ...countryAdminOps.adds,
    ];

    this.internalUserService
      .update(item)
      .pipe(
        switchMap(() => this.generateObsJoin(removeOps$)),
        switchMap(() => this.generateObsJoin(addOps$)),
        switchMap(() => this.userService.getAllUser(this.usersQuery))
      )
      .subscribe(() => {
        internalUserGridItem.onSuccess();
        isSuccess = true;
        this.fetchEntities();

        this.appNotificationService.crudNotify(
          CrudOperation.Update,
          this.entityName,
          isSuccess
        );
      });
  }

  queryUpdate(internalUserGridItem: Partial<QueryOptions<InternalUser>>): void {
    this.query = internalUserGridItem;
    this.fetchEntities();
  }

  onDeleteUserRequested(user: InternalUser): void {
    const signedIn = signedInSnapshot(
      this.currentUserService.currentUserInfo()
    );

    if (isSignedInUsersRow({ id: user.id, email: user.email }, signedIn)) {
      this.appNotificationService.showTranslatedToast(
        this.translateService.instant(
          'admin.user.deleteUserSelfNotAllowedTitle'
        ),
        this.translateService.instant(
          'admin.user.deleteUserSelfNotAllowedDescription'
        )
      );
      return;
    }

    if (isGstarSystemUser(user)) {
      this.appNotificationService.showTranslatedToast(
        this.translateService.instant(
          'admin.user.deleteGstarSystemUserNotAllowedTitle'
        ),
        this.translateService.instant(
          'admin.user.deleteGstarSystemUserNotAllowedDescription'
        )
      );
      return;
    }

    this.modalService.open(
      ConfirmationModalComponent,
      { appModalId: 'internal-user-delete-confirm-modal' },
      {
        title: this.translateService.instant('admin.modal.confirmationTitle'),
        content: this.translateService.instant(
          'admin.user.deleteUserConfirmMessage',
          { fullName: user.fullName }
        ),
        modalId: 'internal-user-delete-confirm-modal',
        confirmationFn: () => this.confirmDeleteInternalUser(user),
      }
    );
  }

  private confirmDeleteInternalUser(user: InternalUser): void {
    this.internalUserService
      .deleteUser(user.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.userService.getAllUser(this.usersQuery))
      )
      .subscribe({
        next: () => {
          this.fetchEntities();
          this.appNotificationService.crudNotify(
            CrudOperation.Delete,
            this.entityName,
            true
          );
        },
        error: () => {
          this.appNotificationService.crudNotify(
            CrudOperation.Delete,
            this.entityName,
            false
          );
        },
      });
  }

  private buildCreateRoleAndCountryObs(
    userId: string,
    item: InternalUserWithRoles
  ): Observable<InternalUserRole | UserCountryAssignment | void>[] {
    const countryAdminRoleId = item.countryAdminRoleId;
    const selectedRoleIds = Object.keys(item.userRoles).filter(
      key => item.userRoles[key]
    );
    const operations: Observable<
      InternalUserRole | UserCountryAssignment | void
    >[] = [];

    for (const userRoleId of selectedRoleIds) {
      if (userRoleId === countryAdminRoleId) {
        operations.push(
          this.internalUserService.addToUserRole(userId, userRoleId)
        );
        for (const assignment of item.countryAssignments ?? []) {
          operations.push(
            this.internalUserService.addCountryAssignment(
              userId,
              assignment.countryId
            )
          );
        }
      } else {
        operations.push(
          this.internalUserService.addToUserRole(userId, userRoleId)
        );
      }
    }

    return operations;
  }

  private buildCountryAdminSaveObs(
    item: InternalUserWithRoles,
    prevItem: InternalUserWithRoles
  ): {
    removes: Observable<void>[];
    adds: Observable<InternalUserRole | UserCountryAssignment>[];
  } {
    const countryAdminRoleId = item.countryAdminRoleId;
    const removes: Observable<void>[] = [];
    const adds: Observable<InternalUserRole | UserCountryAssignment>[] = [];

    if (!countryAdminRoleId) {
      return { removes, adds };
    }

    const didHaveCountryAdmin = !!prevItem.userRoles[countryAdminRoleId];
    const hasCountryAdmin = !!item.userRoles[countryAdminRoleId];

    if (!didHaveCountryAdmin && hasCountryAdmin) {
      adds.push(
        this.internalUserService.addToUserRole(item.id, countryAdminRoleId)
      );
    }

    if (didHaveCountryAdmin && !hasCountryAdmin) {
      removes.push(
        this.internalUserService.removeFromUserRole(
          item.id,
          countryAdminRoleId,
          true
        )
      );
      return { removes, adds };
    }

    if (!hasCountryAdmin) {
      return { removes, adds };
    }

    const originalAssignments = prevItem.countryAssignments ?? [];
    const currentAssignments = item.countryAssignments ?? [];
    const originalCountryIds = new Set(
      originalAssignments.map(assignment => assignment.countryId)
    );
    const currentCountryIds = new Set(
      currentAssignments.map(assignment => assignment.countryId)
    );

    const addedCountryIds = currentAssignments
      .filter(assignment => !originalCountryIds.has(assignment.countryId))
      .map(assignment => assignment.countryId);

    const removedAssignments = originalAssignments.filter(
      assignment => !currentCountryIds.has(assignment.countryId)
    );

    for (const countryId of addedCountryIds) {
      adds.push(
        this.internalUserService.addCountryAssignment(item.id, countryId)
      );
    }

    for (const assignment of removedAssignments) {
      if (assignment.userCountryAssignmentId !== undefined) {
        removes.push(
          this.internalUserService.removeCountryAssignment(
            assignment.userCountryAssignmentId
          )
        );
      }
    }

    return { removes, adds };
  }

  private generateObsJoin(
    arrayObs$: Observable<void | InternalUserRole | UserCountryAssignment>[]
  ): Observable<undefined | [void | InternalUserRole | UserCountryAssignment]> {
    if (!arrayObs$.length) return of(undefined);
    return forkJoin([from(arrayObs$).pipe(concatAll())]);
  }

  private fetchEntities(): void {
    forkJoin({
      internalUsers: this.internalUserService.get({
        ...this.query,
        count: true,
      }),
      clients: this.clientService.get(),
    })
      .pipe(
        switchMap(({ internalUsers, clients }) =>
          this.enrichInternalUsers(internalUsers, clients.items)
        )
      )
      .subscribe((internalUsers: Collection<InternalUser>) => {
        this.entities.set(internalUsers);
      });
  }

  /**
   * Enriches internal users with client names based on their client assignments.
   * @param internalUsers The collection of internal users to be enriched.
   * @param clients The list of clients to map client names.
   * @returns An observable of the enriched internal users collection.
   */
  private enrichInternalUsers(
    internalUsers: Collection<InternalUser>,
    clients: Client[]
  ): Observable<Collection<InternalUser>> {
    if (!internalUsers.items.length) {
      return of(internalUsers);
    }

    const clientNames = new Map(
      clients.map(client => [client.clientId, client.name] as const)
    );

    return forkJoin(
      internalUsers.items.map(user =>
        this.internalUserService.getClientAssignments(user.id).pipe(
          map(engagementAssignments => ({
            ...user,
            engagementAssignments: this.mapEngagementAssignments(
              engagementAssignments,
              clientNames
            ),
          }))
        )
      )
    ).pipe(
      map(items => ({
        ...internalUsers,
        items,
      }))
    );
  }

  /**
   * Maps user engagement assignments to include client names.
   * @param assignments The list of user engagement assignments to be mapped.
   * @param clientNames A map of client IDs to client names.
   * @returns The mapped user engagement assignments with client names.
   */
  private mapEngagementAssignments(
    assignments: UserEngagementAssignment[],
    clientNames: Map<number, string>
  ): UserEngagementAssignment[] {
    return assignments
      .map(assignment => ({
        ...assignment,
        clientName:
          clientNames.get(assignment.clientId) ?? String(assignment.clientId),
      }))
      .sort((left, right) => {
        if (left.isActive !== right.isActive) {
          return Number(right.isActive) - Number(left.isActive);
        }

        return left.clientName.localeCompare(right.clientName);
      });
  }
}
